import { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import { Link } from 'react-router-dom';
import { getProductImage } from '../../utils/imageStorage';
import LocalImageUploader from '../../components/feature/LocalImageUploader';

export default function ProduitBougieLivrePlieAvecLedV2() {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [buttonState, setButtonState] = useState('normal'); // 'normal', 'adding', 'added'
  const productId = 'bougie-avec-led-v2';
  const defaultImage = 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/842983f7d1219d4e05abdf64e978f33f.png';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [showImageUploader, setShowImageUploader] = useState(false);

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  const handleImageUpdate = (newImageUrl: string) => {
    setProductImage(newImageUrl || defaultImage);
    setShowImageUploader(false);
    window.location.reload();
  };

  const product = {
    id: 'bougie-livre-plie-avec-led-v2',
    name: 'Bougie en livre plié – Avec LED',
    price: 30,
    description: "Bougie décorative réalisée à partir d'un véritable livre ancien plié à la main. Chaque pièce est unique et ornée d'un ruban délicat. Une bougie LED amovible est incluse pour une ambiance douce, chaleureuse et sans danger.",
    features: [
      'Fait main à partir de livres anciens',
      'Bougie LED amovible incluse',
      'Ambiance douce et chaleureuse',
      'Sans danger (pas de flamme)',
      'Chaque pièce est unique',
      "Ornée d'un ruban délicat",
      'Création artisanale française',
      'Parfait pour la décoration de Noël'
    ],
    category: 'Collection de Noël',
    subcategory: 'Bougies en livres pliés',
    availability: 'Disponible à la vente'
  };

  const handleAddToCart = () => {
    if (buttonState !== 'normal') return;
    
    setButtonState('adding');
    
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/842983f7d1219d4e05abdf64e978f33f.png',
      description: product.description
    }, quantity);
    
    setButtonState('added');
    
    setTimeout(() => {
      setButtonState('normal');
    }, 2000);
  };

  const getButtonContent = () => {
    switch (buttonState) {
      case 'adding':
        return (
          <>
            <i className="ri-loader-4-line mr-2 animate-spin"></i>
            Ajout en cours...
          </>
        );
      case 'added':
        return (
          <>
            <i className="ri-check-line mr-2"></i>
            Ajouté au panier !
          </>
        );
      default:
        return (
          <>
            <i className="ri-shopping-cart-line mr-2"></i>
            Ajouter au panier • {product.price * quantity}€
          </>
        );
    }
  };

  const getButtonClass = () => {
    switch (buttonState) {
      case 'adding':
        return 'bg-[#B97A72] text-white opacity-75 cursor-wait';
      case 'added':
        return 'bg-green-600 text-white';
      default:
        return 'bg-[#B97A72] text-white hover:bg-[#C49B86] cursor-pointer';
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Breadcrumb */}
      <div className="bg-[#FAF9F8] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#B97A72] hover:text-[#C49B86]">Accueil</Link>
            <i className="ri-arrow-right-s-line text-[#6B5E5A]"></i>
            <Link to="/boutique" className="text-[#B97A72] hover:text-[#C49B86]">Boutique</Link>
            <i className="ri-arrow-right-s-line text-[#6B5E5A]"></i>
            <span className="text-[#6B5E5A]">{product.name}</span>
          </nav>
        </div>
      </div>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Image du produit */}
            <div>
              <div className="w-full h-[500px] bg-white rounded-lg overflow-hidden shadow-lg mb-4">
                <img
                  src={productImage}
                  alt="Bougie en livre plié – Avec LED"
                  className="w-full h-full object-contain"
                />
              </div>
              
              {/* Bouton pour modifier l'image */}
              <div className="text-center">
                <button
                  onClick={() => setShowImageUploader(!showImageUploader)}
                  className="bg-[#B97A72] text-white px-6 py-3 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <i className="ri-image-edit-line mr-2"></i>
                  {showImageUploader ? 'Fermer' : '📸 Modifier l\'image'}
                </button>
              </div>

              {/* Module de changement d'image */}
              {showImageUploader && (
                <div className="mt-6 bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Modifier l'image du produit
                  </h3>
                  <LocalImageUploader
                    productId={productId}
                    currentImage={productImage}
                    onImageUpdate={handleImageUpdate}
                  />
                </div>
              )}
            </div>

            {/* Informations produit */}
            <div className="space-y-6">
              <div>
                <span className="text-sm text-[#B97A72] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.subcategory}
                </span>
                <h1 className="text-4xl font-bold text-[#6B5E5A] mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.name}
                </h1>
                <div className="mt-2 flex items-center space-x-3">
                  <span className="bg-[#B97A72] text-white px-3 py-1 rounded-full text-sm font-medium">
                    Avec LED ✨
                  </span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    {product.availability}
                  </span>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.price}€
                </span>
              </div>

              <p className="text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {product.description}
              </p>

              {/* Caractéristiques */}
              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Caractéristiques
                </h3>
                <ul className="space-y-2">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <i className="ri-check-line text-[#B97A72] mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quantité et ajout au panier */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Quantité
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={buttonState !== 'normal'}
                      className="w-10 h-10 flex items-center justify-center border-2 border-[#B97A72] text-[#B97A72] rounded-lg hover:bg-[#B97A72] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="text-xl font-semibold text-[#6B5E5A] min-w-[3rem] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      disabled={buttonState !== 'normal'}
                      className="w-10 h-10 flex items-center justify-center border-2 border-[#B97A72] text-[#B97A72] rounded-lg hover:bg-[#B97A72] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={buttonState !== 'normal'}
                  className={`w-full py-4 rounded-lg text-lg font-medium whitespace-nowrap transition-all duration-300 ${getButtonClass()}`}
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {getButtonContent()}
                </button>
              </div>

              {/* Informations de livraison */}
              <div className="bg-[#EBD2CF] rounded-lg p-4">
                <div className="flex items-center space-x-3 text-[#6B5E5A]">
                  <i className="ri-truck-line text-[#B97A72]"></i>
                  <span className="text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Livraison gratuite à partir de15 € d'achat
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description détaillée */}
      <div className="mt-16 bg-[#FAF9F8] rounded-lg p-8">
        <h2 className="text-3xl font-bold text-[#B97A72] mb-6 text-center" style={{ fontFamily: 'Allura, cursive' }}>
          Une création artisanale unique
        </h2>
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-[#6B5E5A] leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Cette bougie décorative allie tradition artisanale et modernité. Chaque livre ancien est soigneusement 
            sélectionné puis plié page par page pour créer une forme harmonieuse et unique.
          </p>
          <p className="text-lg text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            La LED amovible offre une sécurité totale : pas de flamme, pas de cire qui coule, juste une belle lumière 
            douce qui met en valeur la beauté du papier plié. Parfaite pour créer une ambiance chaleureuse dans votre intérieur.
          </p>
        </div>
      </div>

      {/* Retour à la boutique */}
      <div className="mt-12 text-center">
        <Link
          to="/boutique"
          className="inline-flex items-center text-[#6B5E5A] hover:text-[#B97A72] transition-colors"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Retour à la boutique
        </Link>
      </div>
    </div>
  );
}
